'use client';

import { ReportModalAverageRatingChartData } from '@/domain/reporting/reporting-modal-charts';
import { FC } from 'react';

type ReportModalTimelineChartProps = {
    chartData: ReportModalAverageRatingChartData | null;
    ariaLabel: string;
    emptyLabel: string;
    lineColor?: string;
    tickFormatter?: (value: number) => string;
};

const ReportModalTimelineChart: FC<ReportModalTimelineChartProps> = ({
    chartData,
    ariaLabel,
    emptyLabel,
    lineColor = '#2563eb',
    tickFormatter = (value) => String(value),
}) => {
    if (!chartData) {
        return <div className="flex h-[116px] items-center justify-center text-xs text-slate-500">{emptyLabel}</div>;
    }

    const markerId = `${ariaLabel.replace(/\s+/g, '-').toLowerCase()}-axis-arrow`;

    return (
        <svg
            className="block h-[116px] w-full"
            width="100%"
            height={chartData.height}
            viewBox={`0 0 ${chartData.width} ${chartData.height}`}
            role="img"
            aria-label={ariaLabel}
        >
            <defs>
                <marker id={markerId} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                    <path d="M0,0 L6,3 L0,6 Z" fill="#94a3b8" />
                </marker>
            </defs>
            {chartData.yTicks.map((tick) => (
                <g key={`${ariaLabel}-tick-${tick.value}`}>
                    <line
                        x1={chartData.axisLeft}
                        y1={tick.y}
                        x2={chartData.axisRight}
                        y2={tick.y}
                        stroke="#e2e8f0"
                        strokeWidth="1"
                    />
                    <text x={chartData.axisLeft - 8} y={tick.y + 3} fontSize="9" fill="#64748b" textAnchor="end">
                        {tickFormatter(tick.value)}
                    </text>
                </g>
            ))}
            <line
                x1={chartData.axisLeft}
                y1={chartData.axisBottom}
                x2={chartData.axisRight}
                y2={chartData.axisBottom}
                stroke="#94a3b8"
                strokeWidth="1"
                markerEnd={`url(#${markerId})`}
            />
            <line
                x1={chartData.axisLeft}
                y1={chartData.axisBottom}
                x2={chartData.axisLeft}
                y2={chartData.axisTop}
                stroke="#94a3b8"
                strokeWidth="1"
                markerEnd={`url(#${markerId})`}
            />
            <polyline
                points={chartData.polyline}
                fill="none"
                stroke={lineColor}
                strokeWidth="2.5"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
            {chartData.points.map((point, index) => (
                <circle
                    key={`${ariaLabel}-point-${index}`}
                    cx={point.x}
                    cy={point.y}
                    r="2.8"
                    fill={lineColor}
                    stroke="#ffffff"
                    strokeWidth="1"
                />
            ))}
            <text x={chartData.firstLabelX} y={chartData.xLabelsY} fontSize="9" fill="#64748b" textAnchor="start">
                {chartData.firstLabel}
            </text>
            <text x={chartData.lastLabelX} y={chartData.xLabelsY} fontSize="9" fill="#64748b" textAnchor="end">
                {chartData.lastLabel}
            </text>
        </svg>
    );
};

export default ReportModalTimelineChart;
